/**
 * Decorative SVG drawer (Phase 7 of pb-section-properties overhaul).
 *
 * The legacy `renderDecorativeSvgPanel` rendered a panel-section with its
 * own header (title + count + Insert button). When mounted inside the
 * Phase 3 drawer, that produced two stacked headers — the drawer's own
 * "Decorative SVG ×" header AND the panel's inline header. The plan called
 * for moving the Insert button into the drawer header and folding the count
 * into the drawer title so the body becomes a clean list.
 *
 * What this drawer wrapper does:
 *
 *   1. Locates the parent drawer's header (`.pb-spi-drawer-header`)
 *   2. Injects a small "Insert" action button before the close button
 *   3. Replaces the drawer title text with `Decorative SVG · N assets`
 *   4. Mounts `renderDecorativeSvgPanel(bodyEl, instanceId, { headerless })`
 *      so the inline header is suppressed; the body is just the list
 *   5. Wires the drawer-header Insert button to the same
 *      `triggerInsertDecorativeSvg` flow the inline button used to call
 *   6. Keeps the title + count in sync via `onCountChange` after every
 *      insert / replace / remove
 *   7. Returns a teardown that removes the injected Insert button so the
 *      next drawer that swaps in starts from a clean header
 *
 * The drawer's own close button is left untouched. Title restoration on
 * swap is handled by `drawer.js` (it overwrites the title in `_swapDrawer`).
 */

import { renderDecorativeSvgPanel, triggerInsertDecorativeSvg } from '../decorative-svg-panel.js';

const BASE_TITLE = 'Decorative SVG';

/**
 * Mount the Decorative SVG drawer.
 *
 * @param {HTMLElement} container - drawer body element
 * @param {{ instanceId: string }} ctx
 * @returns {() => void} teardown
 */
export function renderSvgDrawer(container, { instanceId }) {
  if (!container || !instanceId) {
    console.warn('[svg-drawer] missing container/instanceId');
    return () => {};
  }

  // Drawer body is `.pb-spi-drawer-body`; its parent is the drawer container
  // which holds the header. Resolve once on mount; we reuse this on every
  // count update.
  const drawerEl = container.closest('.pb-spi-drawer');
  const headerEl = drawerEl?.querySelector('.pb-spi-drawer-header') || null;
  const titleEl = drawerEl?.querySelector('.pb-spi-drawer-title') || null;
  const closeBtn = drawerEl?.querySelector('.pb-spi-drawer-close') || null;

  // Inject the Insert button into the drawer header (before the close button).
  // If the drawer chrome isn't where we expect (tests / unusual mount paths),
  // fall back to the legacy inline header in the body so the user always has
  // an Insert affordance.
  let insertBtn = null;
  const hasChrome = !!(headerEl && closeBtn);
  if (hasChrome) {
    insertBtn = document.createElement('button');
    insertBtn.type = 'button';
    insertBtn.className = 'pb-spi-svg-drawer-insert';
    insertBtn.setAttribute('aria-label', 'Insert decorative SVG');
    insertBtn.dataset.svgDrawerInsert = '';
    insertBtn.innerHTML = '<span aria-hidden="true">＋</span><span>Insert</span>';
    headerEl.insertBefore(insertBtn, closeBtn);
  }

  function setTitleWithCount(label) {
    if (!titleEl) return;
    titleEl.textContent = `${BASE_TITLE} · ${label}`;
  }

  // Single mount call with the right options based on chrome detection.
  // Avoids a double-render flash when the fallback path triggers.
  const panelHandle = hasChrome
    ? renderDecorativeSvgPanel(container, instanceId, {
        headerless: true,
        onCountChange: (_count, label) => setTitleWithCount(label)
      })
    : renderDecorativeSvgPanel(container, instanceId, {});

  insertBtn?.addEventListener('click', (e) => {
    triggerInsertDecorativeSvg(instanceId, e.currentTarget, () => panelHandle?.rerender?.());
  });

  // ── Teardown ───────────────────────────────────────────────────────────
  return function teardown() {
    if (insertBtn && insertBtn.parentNode) {
      insertBtn.parentNode.removeChild(insertBtn);
    }
    insertBtn = null;
    // The drawer's title is reset by drawer.js whenever a fresh drawer
    // mounts or a swap happens, so we don't need to restore it explicitly.
    // The list itself is torn down when drawer.js empties the body.
  };
}
